import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaUsers,
  FaSearch,
  FaSync,
  FaUserPlus,
  FaTimes,
  FaTrash,
  FaEye,
  FaEyeSlash,
  FaChevronUp,
  FaChevronDown,
  FaCircle,
  FaPlus,
} from 'react-icons/fa';
import ConfirmDialog from './ConfirmDialog';
import DiscordUserChip from './DiscordUserChip';
import { TeamBadge, TeamBadgeList } from '../TeamBadge/TeamBadge';
import TeamMemberCard from '../TeamBadge/TeamMemberCard';
import TeamVerifiedBadge from '../TeamBadge/TeamVerifiedBadge';
import {
  TEAM_BADGE_CATALOG,
  TEAM_BADGE_ICONS,
  TEAM_BADGE_TONES,
  TEAM_SOCIAL_KEYS,
  resolveTeamBadges,
  formatLastSeenLabel,
} from '../../config/teamBadges';

import './TeamPanel.css';

const emptyForm = () => ({
  discordId: '',
  displayName: '',
  title: 'Staff',
  bio: '',
  badges: ['staff'],
  visible: true,
  verified: false,
  socials: {
    discord: '',
    twitter: '',
    youtube: '',
    twitch: '',
    github: '',
    instagram: '',
  },
  avatarUrl: '',
});

const emptyBadgeForm = () => ({
  label: '',
  description: '',
  icon: 'star',
  tone: 'violet',
  color: '',
});

const socialsFromMember = (member) => ({
  discord: member?.socials?.discord || '',
  twitter: member?.socials?.twitter || '',
  youtube: member?.socials?.youtube || '',
  twitch: member?.socials?.twitch || '',
  github: member?.socials?.github || '',
  instagram: member?.socials?.instagram || '',
});

const setSocial = (setter, key, value) => {
  setter((prev) => ({
    ...prev,
    socials: { ...prev.socials, [key]: value },
  }));
};

const TeamPanel = ({
  stats,
  members = [],
  total = 0,
  catalog,
  visibilityFilter = 'all',
  query = '',
  loading = false,
  actionBusy = null,
  message = '',
  error = '',
  canEdit = true,
  canDelete = true,
  onVisibilityFilter,
  onQueryChange,
  onSearch,
  onRefresh,
  onSelect,
  selected,
  onCloseDetail,
  onCreate,
  onUpdate,
  onDelete,
  onReorder,
  createOpen = false,
  onToggleCreate,
  guildMembers = [],
  guildMembersLoading = false,
  guildMembersError = '',
  onReloadMembers,
  onCreateBadge,
  onDeleteBadge,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);
  const [badgeForm, setBadgeForm] = useState(emptyBadgeForm);
  const [badgeModalOpen, setBadgeModalOpen] = useState(false);
  const [badgeModalTarget, setBadgeModalTarget] = useState('create'); // create | edit
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberMenuOpen, setMemberMenuOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const memberPickerRef = useRef(null);

  const badges = catalog?.badges?.length ? catalog.badges : TEAM_BADGE_CATALOG;
  const iconOptions = catalog?.icons?.length ? catalog.icons : TEAM_BADGE_ICONS;
  const toneOptions = catalog?.tones?.length ? catalog.tones : TEAM_BADGE_TONES;

  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    return guildMembers
      .filter((m) => {
        if (!q) return true;
        const hay = [m.displayName, m.globalName, m.username, m.nick, m.id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 80);
  }, [guildMembers, memberQuery]);

  const rawIdDraft = String(memberQuery || '').trim();
  const canUseRawId = /^\d{5,32}$/.test(rawIdDraft);

  const createPreview = useMemo(
    () => ({
      displayName:
        form.displayName ||
        selectedMember?.displayName ||
        selectedMember?.globalName ||
        selectedMember?.username ||
        'Novo membro',
      title: form.title || 'Staff',
      bio: form.bio,
      avatarUrl: form.avatarUrl || selectedMember?.avatarUrl || '',
      discordId: form.discordId,
      socials: form.socials,
      badges: resolveTeamBadges(form.badges, badges),
      verified: Boolean(form.verified),
      online: Boolean(selectedMember?.online),
      lastSeen: selectedMember?.lastSeen || null,
    }),
    [form, selectedMember, badges]
  );

  const editPreview = useMemo(
    () => ({
      displayName: editForm.displayName || selected?.displayName || 'Membro',
      title: editForm.title || 'Staff',
      bio: editForm.bio,
      avatarUrl: editForm.avatarUrl || selected?.avatarUrl || '',
      discordId: editForm.discordId || selected?.discordId,
      socials: editForm.socials,
      badges: resolveTeamBadges(editForm.badges, badges),
      verified: Boolean(editForm.verified),
      online: Boolean(selected?.online),
      lastSeen: selected?.lastSeen || null,
    }),
    [editForm, selected, badges]
  );

  const badgePreview = useMemo(
    () => ({
      id: 'preview',
      label: badgeForm.label || 'Nova badge',
      description: badgeForm.description || '',
      icon: badgeForm.icon || 'star',
      tone: badgeForm.tone || 'violet',
      color: badgeForm.color || undefined,
      custom: true,
    }),
    [badgeForm]
  );

  useEffect(() => {
    if (!selected) return;
    setEditForm({
      discordId: selected.discordId || '',
      displayName: selected.displayName || '',
      title: selected.title || 'Staff',
      bio: selected.bio || '',
      badges: selected.badgeIds || selected.badges?.map((b) => b.id) || [],
      visible: selected.visible !== false,
      verified: selected.verified === true,
      socials: socialsFromMember(selected),
      avatarUrl: selected.avatarUrl || '',
    });
  }, [selected]);

  useEffect(() => {
    if (createOpen) return;
    setForm(emptyForm());
    setSelectedMember(null);
    setMemberQuery('');
    setMemberMenuOpen(false);
  }, [createOpen]);

  useEffect(() => {
    if (!memberMenuOpen) return undefined;
    const onDoc = (e) => {
      if (memberPickerRef.current && !memberPickerRef.current.contains(e.target)) {
        setMemberMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMemberMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [memberMenuOpen]);

  const pickMember = (m) => {
    setSelectedMember(m);
    setMemberQuery(m.displayName || m.username || m.id);
    setMemberMenuOpen(false);
    setForm((prev) => ({
      ...prev,
      discordId: m.id,
      displayName: prev.displayName || m.displayName || m.globalName || m.username || '',
      avatarUrl: prev.avatarUrl || m.avatarUrl || '',
    }));
  };

  const toggleBadge = (setter, id) => {
    setter((prev) => {
      const has = prev.badges.includes(id);
      const next = has
        ? prev.badges.filter((b) => b !== id)
        : [...prev.badges, id].slice(0, 8);
      return { ...prev, badges: next };
    });
  };

  const moveMember = (id, direction) => {
    const ids = members.map((m) => m.id);
    const idx = ids.indexOf(id);
    if (idx < 0) return;
    const swap = idx + direction;
    if (swap < 0 || swap >= ids.length) return;
    const next = [...ids];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onReorder?.(next);
  };

  const openBadgeModal = (target) => {
    setBadgeModalTarget(target);
    setBadgeForm(emptyBadgeForm());
    setBadgeModalOpen(true);
  };

  const closeBadgeModal = () => {
    setBadgeModalOpen(false);
    setBadgeForm(emptyBadgeForm());
  };

  const submitBadge = async () => {
    const label = badgeForm.label.trim();
    if (!label) return;
    const badge = await onCreateBadge?.({
      label,
      description: badgeForm.description.trim(),
      icon: badgeForm.icon,
      tone: badgeForm.tone,
      color: badgeForm.color.trim() || undefined,
    });
    if (badge?.id) {
      const setter = badgeModalTarget === 'edit' ? setEditForm : setForm;
      setter((prev) => ({
        ...prev,
        badges: prev.badges.includes(badge.id)
          ? prev.badges
          : [...prev.badges, badge.id].slice(0, 8),
      }));
    }
    closeBadgeModal();
  };

  const renderBadgePicker = (selectedIds, setter, target) => (
    <fieldset className="admin-fieldset admin-field--full team-detail__badges-field">
      <legend>Badges</legend>
      <div className="team-badge-picker">
        {badges.map((b) => {
          const active = selectedIds.includes(b.id);
          return (
            <div key={b.id} className="team-badge-picker__wrap">
              <button
                type="button"
                className={`team-badge-picker__item${active ? ' is-active' : ''}`}
                onClick={() => toggleBadge(setter, b.id)}
                title={b.description || b.label}
              >
                <TeamBadge badge={b} size="sm" />
              </button>
              {b.custom && (
                <button
                  type="button"
                  className="team-badge-picker__remove"
                  title={`Excluir badge ${b.label}`}
                  aria-label={`Excluir badge ${b.label}`}
                  disabled={actionBusy === 'delete_badge'}
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDialog({
                      title: 'Excluir badge?',
                      message: `Remover a badge “${b.label}”? Membros que a usam deixarão de exibi-la.`,
                      confirmLabel: 'Excluir',
                      tone: 'danger',
                      onConfirm: () => onDeleteBadge?.(b.id),
                    });
                  }}
                >
                  <FaTimes />
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          className="team-badge-picker__add"
          onClick={() => openBadgeModal(target)}
          title="Criar badge personalizada"
          aria-label="Criar badge personalizada"
        >
          <FaPlus />
        </button>
      </div>
    </fieldset>
  );

  const renderSocialFields = (values, setter) => (
    <fieldset className="admin-fieldset admin-field--full team-social-fieldset">
      <legend>Redes sociais</legend>
      <div className="team-social-grid">
        {TEAM_SOCIAL_KEYS.map((s) => (
          <label key={s.id} className="admin-field">
            <span>{s.label}</span>
            <input
              type="text"
              value={values.socials?.[s.id] || ''}
              onChange={(e) => setSocial(setter, s.id, e.target.value)}
              placeholder={s.placeholder}
            />
          </label>
        ))}
      </div>
    </fieldset>
  );

  return (
    <section className="admin-section team-panel">
      <div className="team-panel__head">
        <div>
          <h1>
            <FaUsers /> Equipe do site
          </h1>
          <p className="admin-muted">
            Gerencie quem aparece na página pública da equipe e quais badges cada perfil recebe.
            Online = Discord ID com conta vinculada e jogando no servidor (últimos 2 min).
          </p>
        </div>
        <div className="team-panel__head-actions">
          <button
            type="button"
            className="admin-btn admin-btn--ghost"
            onClick={onRefresh}
            disabled={loading}
          >
            <FaSync className={loading ? 'admin-spin' : ''} />
            Atualizar
          </button>
          {canEdit && (
          <button
            type="button"
            className={`admin-btn${createOpen ? ' admin-btn--ghost' : ' admin-btn--primary'}`}
            onClick={onToggleCreate}
          >
            {createOpen ? <FaTimes /> : <FaUserPlus />}
            {createOpen ? 'Fechar' : 'Adicionar membro'}
          </button>
          )}
        </div>
      </div>

      {(message || error) && (
        <div className={error ? 'admin-alert' : 'admin-success'} role="status">
          {error || message}
        </div>
      )}

      <div className="admin-stats admin-stats--4 team-stats">
        <div className="admin-stat">
          <span className="admin-stat__label">Total</span>
          <strong className="admin-stat__value">{stats?.total ?? total ?? 0}</strong>
          <span className="admin-stat__sub">membros cadastrados</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Visíveis</span>
          <strong className="admin-stat__value">{stats?.visible ?? 0}</strong>
          <span className="admin-stat__sub">na página /equipe</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Ocultos</span>
          <strong className="admin-stat__value">{stats?.hidden ?? 0}</strong>
          <span className="admin-stat__sub">só no painel</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Online</span>
          <strong className="admin-stat__value">{stats?.online ?? 0}</strong>
          <span className="admin-stat__sub">no servidor (conta vinculada)</span>
        </div>
      </div>

      {createOpen && (
        <div className="team-create">
          <header>
            <h2>Novo membro</h2>
            <p>Escolha alguém do Discord da guild ou cole o ID manualmente.</p>
          </header>

          <div className="team-create__layout">
            <div className="admin-form team-create__form">
              <label className="admin-field">
                <span>Membro Discord</span>
                <div className="team-member-picker" ref={memberPickerRef}>
                  <div className="team-member-picker__row">
                    <input
                      type="text"
                      value={memberQuery}
                      placeholder="Buscar por nome ou colar Discord ID…"
                      onChange={(e) => {
                        setMemberQuery(e.target.value);
                        setMemberMenuOpen(true);
                        setSelectedMember(null);
                        const raw = e.target.value.trim();
                        if (/^\d{5,32}$/.test(raw)) {
                          setForm((prev) => ({ ...prev, discordId: raw }));
                        }
                      }}
                      onFocus={() => {
                        setMemberMenuOpen(true);
                        if (!guildMembers.length) onReloadMembers?.();
                      }}
                    />
                    <button
                      type="button"
                      className="admin-btn admin-btn--ghost"
                      onClick={() => onReloadMembers?.()}
                      disabled={guildMembersLoading}
                    >
                      <FaSync className={guildMembersLoading ? 'admin-spin' : ''} />
                    </button>
                  </div>
                  {memberMenuOpen && (
                    <div className="team-member-picker__menu" role="listbox">
                      {guildMembersError && (
                        <p className="admin-muted">{guildMembersError}</p>
                      )}
                      {canUseRawId && !selectedMember && (
                        <button
                          type="button"
                          className="team-member-picker__option"
                          onClick={() => {
                            setForm((prev) => ({ ...prev, discordId: rawIdDraft }));
                            setSelectedMember({
                              id: rawIdDraft,
                              displayName: rawIdDraft,
                            });
                            setMemberMenuOpen(false);
                          }}
                        >
                          Usar ID <strong>{rawIdDraft}</strong>
                        </button>
                      )}
                      {filteredMembers.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          className="team-member-picker__option"
                          onClick={() => pickMember(m)}
                        >
                          <DiscordUserChip user={m} />
                        </button>
                      ))}
                      {!filteredMembers.length && !canUseRawId && (
                        <p className="admin-muted">Nenhum membro encontrado.</p>
                      )}
                    </div>
                  )}
                </div>
              </label>

              <label className="admin-field">
                <span>Nome exibido</span>
                <input
                  type="text"
                  value={form.displayName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, displayName: e.target.value }))
                  }
                  placeholder="Opcional — usa o Discord se vazio"
                />
              </label>

              <label className="admin-field">
                <span>Cargo / título</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, title: e.target.value }))
                  }
                  placeholder="Ex.: Fundador, Admin, Builder…"
                />
              </label>

              <label className="admin-field admin-field--full">
                <span>Bio</span>
                <textarea
                  rows={3}
                  value={form.bio}
                  onChange={(e) => setForm((p) => ({ ...p, bio: e.target.value }))}
                  placeholder="Uma frase curta sobre o membro"
                />
              </label>

              {renderBadgePicker(form.badges, setForm, 'create')}

              {renderSocialFields(form, setForm)}

              <label className="admin-switch team-visible-switch">
                <span>
                  <strong>Visível na página pública</strong>
                  <small>Aparece em /equipe quando ativo</small>
                </span>
                <input
                  type="checkbox"
                  checked={form.visible}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, visible: e.target.checked }))
                  }
                />
              </label>

              <label className="admin-switch team-visible-switch">
                <span>
                  <strong>Verificado (Vih&apos;s Studio)</strong>
                  <small>Selo exclusivo para membros do Vih&apos;s Studio / Vih&apos;s</small>
                </span>
                <input
                  type="checkbox"
                  checked={form.verified}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, verified: e.target.checked }))
                  }
                />
              </label>

              <div className="team-create__actions">
                <button
                  type="button"
                  className="admin-btn admin-btn--primary"
                  disabled={actionBusy === 'create' || !form.discordId}
                  onClick={() => onCreate?.(form)}
                >
                  <FaUserPlus />
                  {actionBusy === 'create' ? 'Salvando…' : 'Adicionar'}
                </button>
              </div>
            </div>

            <aside className="team-preview">
              <h3>Prévia em /equipe</h3>
              <TeamMemberCard member={createPreview} compact />
            </aside>
          </div>
        </div>
      )}

      <div className="team-toolbar">
        <div className="admin-filters">
          {[
            { id: 'all', label: 'Todos' },
            { id: 'visible', label: 'Visíveis' },
            { id: 'hidden', label: 'Ocultos' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`admin-chip${visibilityFilter === opt.id ? ' admin-chip--active' : ''}`}
              onClick={() => onVisibilityFilter?.(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <form
          className="admin-search"
          onSubmit={(e) => {
            e.preventDefault();
            onSearch?.();
          }}
        >
          <FaSearch />
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange?.(e.target.value)}
            placeholder="Buscar por nome, cargo ou Discord ID…"
          />
          <button type="submit" className="admin-btn admin-btn--ghost">
            Buscar
          </button>
        </form>
      </div>

      <div className="team-layout">
        {selected && (
          <aside className="team-detail">
            <header className="team-detail__head">
              <div className="team-detail__who">
                {selected.avatarUrl && (
                  <img src={selected.avatarUrl} alt="" width={72} height={72} />
                )}
                <div>
                  <h2 className="team-detail__name">
                    <span>{selected.displayName}</span>
                    {selected.verified && <TeamVerifiedBadge size="md" />}
                  </h2>
                  <p className="admin-muted">{selected.title}</p>
                  <TeamBadgeList
                    badges={selected.badges}
                    size="md"
                    className="team-detail__badges"
                  />
                </div>
              </div>
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm"
                onClick={onCloseDetail}
                aria-label="Fechar"
              >
                <FaTimes /> Fechar
              </button>
            </header>

            <div className="team-detail__layout">
              <div className="admin-form team-detail__form">
                <label className="admin-field">
                  <span>Nome exibido</span>
                  <input
                    type="text"
                    value={editForm.displayName}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, displayName: e.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Cargo / título</span>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, title: e.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Discord ID</span>
                  <input
                    type="text"
                    value={editForm.discordId}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, discordId: e.target.value }))
                    }
                  />
                </label>
                <label className="admin-field team-detail__bio">
                  <span>Bio</span>
                  <textarea
                    rows={3}
                    value={editForm.bio}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, bio: e.target.value }))
                    }
                  />
                </label>

                {renderBadgePicker(editForm.badges, setEditForm, 'edit')}

                {renderSocialFields(editForm, setEditForm)}

                <label className="admin-switch team-visible-switch team-detail__visible">
                  <span>
                    <strong>Visível na página pública</strong>
                    <small>Aparece em /equipe quando ativo</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={editForm.visible}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, visible: e.target.checked }))
                    }
                  />
                </label>

                <label className="admin-switch team-visible-switch team-detail__visible">
                  <span>
                    <strong>Verificado (Vih&apos;s Studio)</strong>
                    <small>Selo exclusivo para membros do Vih&apos;s Studio / Vih&apos;s</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={editForm.verified}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, verified: e.target.checked }))
                    }
                  />
                </label>
              </div>

              <aside className="team-preview">
                <h3>Prévia em /equipe</h3>
                <TeamMemberCard member={editPreview} compact />
              </aside>
            </div>

            <div className="team-detail__actions">
              {canEdit && (
              <button
                type="button"
                className="admin-btn admin-btn--primary"
                disabled={actionBusy === 'update'}
                onClick={() => onUpdate?.(selected.id, editForm)}
              >
                {actionBusy === 'update' ? 'Salvando…' : 'Salvar alterações'}
              </button>
              )}
              {canDelete && (
              <button
                type="button"
                className="admin-btn admin-btn--danger"
                disabled={actionBusy === 'delete'}
                onClick={() =>
                  setConfirmDialog({
                    title: 'Remover da equipe?',
                    message: `Isso remove ${selected.displayName} da página e do painel. Continuar?`,
                    confirmLabel: 'Remover',
                    tone: 'danger',
                    onConfirm: () => onDelete?.(selected.id),
                  })
                }
              >
                <FaTrash /> Remover
              </button>
              )}
            </div>
          </aside>
        )}

        <div className="admin-table-wrap team-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Ordem</th>
                <th>Membro</th>
                <th>Cargo</th>
                <th>Badges</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {!members.length ? (
                <tr>
                  <td colSpan={5} className="admin-empty">
                    {loading ? 'Carregando…' : 'Nenhum membro cadastrado ainda.'}
                  </td>
                </tr>
              ) : (
                members.map((m, index) => (
                  <tr
                    key={m.id}
                    className={`admin-table__row${selected?.id === m.id ? ' is-selected' : ''}`}
                    onClick={() => onSelect?.(m)}
                  >
                    <td>
                      <div className="team-order">
                        <button
                          type="button"
                          className="team-icon-btn"
                          disabled={index === 0 || actionBusy === 'reorder'}
                          onClick={(e) => {
                            e.stopPropagation();
                            moveMember(m.id, -1);
                          }}
                          aria-label="Subir"
                        >
                          <FaChevronUp />
                        </button>
                        <span>{index + 1}</span>
                        <button
                          type="button"
                          className="team-icon-btn"
                          disabled={
                            index === members.length - 1 || actionBusy === 'reorder'
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            moveMember(m.id, 1);
                          }}
                          aria-label="Descer"
                        >
                          <FaChevronDown />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="team-row-user">
                        {m.avatarUrl ? (
                          <img src={m.avatarUrl} alt="" width={40} height={40} />
                        ) : null}
                        <div className="team-row-user__text">
                          <strong className="team-row-user__name">
                            <span>{m.displayName}</span>
                            {m.verified && (
                              <TeamVerifiedBadge size="sm" tooltipPlacement="bottom" />
                            )}
                          </strong>
                          <span className="admin-muted">{m.discordId}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="team-row-title">{m.title}</span>
                    </td>
                    <td>
                      <TeamBadgeList
                        badges={m.badges}
                        size="sm"
                        showLabel
                        className="team-row-badges"
                      />
                    </td>
                    <td>
                      <div className="team-row-status">
                        <span
                          className={`team-status-badge team-status-badge--${m.visible ? 'ok' : 'muted'}`}
                        >
                          {m.visible ? (
                            <>
                              <FaEye /> Visível
                            </>
                          ) : (
                            <>
                              <FaEyeSlash /> Oculto
                            </>
                          )}
                        </span>
                        {m.online ? (
                          <span className="team-status-badge team-status-badge--online">
                            <FaCircle /> Online
                          </span>
                        ) : formatLastSeenLabel(m.lastSeen) ? (
                          <span
                            className="team-status-badge team-status-badge--seen"
                            title={m.lastSeen ? new Date(m.lastSeen).toLocaleString('pt-BR') : undefined}
                          >
                            {formatLastSeenLabel(m.lastSeen)}
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {badgeModalOpen && (
        <div
          className="admin-confirm"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && actionBusy !== 'create_badge') {
              closeBadgeModal();
            }
          }}
        >
          <div
            className="admin-confirm__card admin-confirm__card--primary team-badge-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-badge-modal-title"
          >
            <header className="admin-confirm__head">
              <h2 id="team-badge-modal-title">Nova badge personalizada</h2>
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm"
                onClick={closeBadgeModal}
                disabled={actionBusy === 'create_badge'}
                aria-label="Fechar"
              >
                <FaTimes aria-hidden="true" />
              </button>
            </header>

            <p className="admin-confirm__body">
              A badge entra no catálogo e já fica selecionada neste membro.
            </p>

            <div className="admin-form team-badge-modal__form">
              <label className="admin-field">
                <span>Nome</span>
                <input
                  type="text"
                  value={badgeForm.label}
                  onChange={(e) =>
                    setBadgeForm((p) => ({ ...p, label: e.target.value }))
                  }
                  placeholder="Ex.: Streamer, Designer…"
                  autoFocus
                />
              </label>
              <label className="admin-field">
                <span>Descrição</span>
                <input
                  type="text"
                  value={badgeForm.description}
                  onChange={(e) =>
                    setBadgeForm((p) => ({ ...p, description: e.target.value }))
                  }
                  placeholder="Texto curto ao passar o mouse"
                />
              </label>
              <label className="admin-field">
                <span>Ícone</span>
                <select
                  value={badgeForm.icon}
                  onChange={(e) =>
                    setBadgeForm((p) => ({ ...p, icon: e.target.value }))
                  }
                >
                  {iconOptions.map((icon) => (
                    <option key={icon} value={icon}>
                      {icon}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span>Tom</span>
                <select
                  value={badgeForm.tone}
                  onChange={(e) =>
                    setBadgeForm((p) => ({ ...p, tone: e.target.value }))
                  }
                >
                  {toneOptions.map((tone) => (
                    <option key={tone} value={tone}>
                      {tone}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field admin-field--full">
                <span>Cor (hex, opcional)</span>
                <input
                  type="text"
                  value={badgeForm.color}
                  onChange={(e) =>
                    setBadgeForm((p) => ({ ...p, color: e.target.value }))
                  }
                  placeholder="#a78bfa"
                />
              </label>
              <div className="team-badge-modal__preview admin-field--full">
                <span className="admin-muted">Prévia</span>
                <TeamBadge badge={badgePreview} size="md" />
              </div>
            </div>

            <div className="admin-confirm__actions">
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                onClick={closeBadgeModal}
                disabled={actionBusy === 'create_badge'}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--primary"
                disabled={actionBusy === 'create_badge' || !badgeForm.label.trim()}
                onClick={submitBadge}
              >
                <FaPlus />
                {actionBusy === 'create_badge' ? 'Criando…' : 'Criar badge'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        tone={confirmDialog?.tone || 'danger'}
        busy={Boolean(actionBusy)}
        onCancel={() => setConfirmDialog(null)}
        onConfirm={() => {
          const fn = confirmDialog?.onConfirm;
          setConfirmDialog(null);
          fn?.();
        }}
      />
    </section>
  );
};

export default TeamPanel;
